import { Component, OnInit, OnDestroy, Output, EventEmitter, inject, effect, Input } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { FullViewService } from '../../services/full-view.service';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { MaintenanceCostingService } from '../../services/maintenance-costing.service';
import { MaintenanceContractColumnService } from '../../services/maintenance-contract-column.service';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { ContractFilter } from '../../enums/maintenance-costing.enum';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { MaintenanceCostingRequest } from '../../models/maintenance-costing-request.model';
import { GetMaintenanceContractsResponse } from '../../models/maintenance-costing-response.model';
import { ContractCell, ContractColumn, ContractRow } from '../../models/maintenance-costing.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-outsourced-maintenance-contract',
  standalone: true,
  imports: [
    CommonModule,
    SearchComponent,
    NgbDropdownModule,
    PaginationComponent,
    EmptyStateComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  providers: [DatePipe],
  templateUrl: './outsourced-maintenance-contract.component.html',
  styleUrl: './outsourced-maintenance-contract.component.scss'
})
export class OutsourcedMaintenanceContractComponent implements OnInit, OnDestroy {
  @Output() fullViewChange = new EventEmitter<boolean>();

  private readonly fullViewService = inject(FullViewService);
  private readonly costingService = inject(MaintenanceCostingService);
  private readonly columnService = inject(MaintenanceContractColumnService);
  private readonly filterService = inject(FilterService);
  private readonly transloco = inject(TranslocoService);
  private readonly datePipe = inject(DatePipe);
  private cancelSubject: Subject<void> = new Subject<void>();
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);
  private widgetFilter: WidgetFilterData = {} as WidgetFilterData;
  private isInitialised: boolean = false;

  readonly previewPageSize: number = 5;
  readonly fullViewPageSize: number = 12;

  // Contract type names as returned by the maintenance contracts API (full name or abbreviation),
  // mapped to their label key and badge class. Unknown values render raw with no badge.
  private readonly contractTypes: Record<string, { labelKey: string; badgeClass: string }> = {
    'Service Only': { labelKey: 'maintenanceCosting.outsourced.contractTypes.serviceOnly', badgeClass: 'contract-type-service-only' },
    'Full Maintenance Contract': { labelKey: 'maintenanceCosting.outsourced.contractTypes.fullMaintenanceContract', badgeClass: 'contract-type-fmc' },
    'FMC': { labelKey: 'maintenanceCosting.outsourced.contractTypes.fullMaintenanceContract', badgeClass: 'contract-type-fmc' },
    'Full Maintenance Lease': { labelKey: 'maintenanceCosting.outsourced.contractTypes.fullMaintenanceLease', badgeClass: 'contract-type-fml' },
    'FML': { labelKey: 'maintenanceCosting.outsourced.contractTypes.fullMaintenanceLease', badgeClass: 'contract-type-fml' }
  };

  searchTerm: string = '';
  isLoading: boolean = true;
  isFullView: boolean = this.fullViewService.isFullView();
  currencySymbol: string = '';
  currencyCode: string = '';
  distanceSymbol: string = '';

  contractTypeFilter = ContractFilter;
  selectedContractType: ContractFilter = ContractFilter.All;

  columnKeys: string[] = [];
  visibleColumns: ContractColumn[] = [];
  contracts: GetMaintenanceContractsResponse[] = [];
  rows: ContractRow[] = [];

  page: number = 1;
  totalPages: number = 0;
  totalCount: number = 0;

  contractTypeLabelKeys: Record<ContractFilter, string> = {
    [ContractFilter.All]: 'maintenanceCosting.outsourced.filter.all',
    [ContractFilter.PerAssetContracts]: 'maintenanceCosting.outsourced.filter.perAssetContract',
    [ContractFilter.MultiAssetContracts]: 'maintenanceCosting.outsourced.filter.multiAssetContract'
  };

  constructor() {
    const userSettings = this.authService.getUserSettings();
    this.currencyCode = userSettings?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
    this.distanceSymbol = userSettings?.distanceSymbol ?? 'km';

    this.applyColumns(null);

    this.filterService.filterUpdated$
      .pipe(takeUntilDestroyed())
      .subscribe((filter) => {
        this.widgetFilter = filter;
        this.page = 1;
        this.loadContracts();
      });

    effect(() => {
      const active = this.fullViewService.isFullView();

      if (this.isFullView !== active) {
        this.isFullView = active;
        this.page = 1;
        this.loadContracts();
      }
    });
  }

  @Input() set selectedColumns(columns: string[] | null | undefined) {
    const previousKeys = this.columnKeys;
    this.applyColumns(columns);
    if (this.isInitialised && previousKeys.join() !== this.columnKeys.join()) {
      this.page = 1;
      this.loadContracts();
    }
  }

  get pageSize(): number {
    return this.isFullView ? this.fullViewPageSize : this.previewPageSize;
  }

  get contractTypeLabel(): string {
    return this.transloco.translate(this.contractTypeLabelKeys[this.selectedContractType]);
  }

  get showingCount(): number {
    return Math.min(this.page * this.pageSize, this.totalCount);
  }

  get columnCount(): number {
    return this.visibleColumns.length;
  }

  async ngOnInit(): Promise<void> {
    this.isInitialised = true;
    this.widgetFilter = this.filterService.filter;
    await this.loadContracts();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private applyColumns(columns: string[] | null | undefined): void {
    this.visibleColumns = this.columnService.resolveColumns(columns);
    this.columnKeys = columns?.length ? [...columns] : this.columnService.getSelectedColumns();
    this.rows = this.buildRows(this.contracts);
  }

  private async loadContracts(): Promise<void> {
    const { accountIds, startDate, endDate } = this.widgetFilter;

    if (!startDate || !endDate) {
      return;
    }

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: MaintenanceCostingRequest = {
        Columns: this.visibleColumns.map(column => column.id),
        contractType: this.selectedContractType === ContractFilter.All ? undefined : this.selectedContractType,
        accountIds: accountIds ?? [],
        timeFrom: startDate,
        timeTo: endDate,
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm
      };

      const response = await this.costingService.getMaintenanceCostingContractRequest(request, this.cancelSubject);
      this.contracts = response.data?.results ?? [];
      this.rows = this.buildRows(this.contracts);
      this.totalCount = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.contracts = [];
          this.rows = [];
          this.totalCount = 0;
          this.totalPages = 0;
          this.isLoading = false;
        }
      });
    }
  }

  private buildRows(contracts: GetMaintenanceContractsResponse[]): ContractRow[] {
    return contracts.map((contract, index) => ({
      index,
      cells: this.visibleColumns.map(column => this.buildCell(contract, column))
    }));
  }

  private buildCell(contract: GetMaintenanceContractsResponse, column: ContractColumn): ContractCell {
    const value = contract[column.field];

    if (value === null || value === undefined || value === '') {
      return { column, value: '-', badgeClass: null };
    }

    switch (column.format) {
      case 'contractType': {
        const contractType = this.contractTypes[`${value}`];
        return contractType
          ? { column, value: this.transloco.translate(contractType.labelKey), badgeClass: contractType.badgeClass }
          : { column, value: `${value}`, badgeClass: null };
      }
      case 'date':
        return { column, value: this.datePipe.transform(value as Date, 'dd/MM/yyyy') ?? '-', badgeClass: null };
      case 'currency':
        return { column, value: this.formatCurrency(value as number), badgeClass: null };
      case 'distance':
        return { column, value: `${(value as number).toLocaleString()} ${this.distanceSymbol}`, badgeClass: null };
      case 'costPerDistance':
        return { column, value: `${this.currencySymbol}${(value as number).toLocaleString('default', { maximumFractionDigits: 2 })}/${this.distanceSymbol}`, badgeClass: null };
      default:
        return { column, value: `${value}`, badgeClass: null };
    }
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.page = 1;
    this.loadContracts();
  }

  onContractTypeFilter(contractType: ContractFilter): void {
    if (this.selectedContractType === contractType) {
      return;
    }

    this.selectedContractType = contractType;
    this.page = 1;
    this.loadContracts();
  }

  onViewAll(): void {
    this.fullViewService.enableFullView();
  }

  onBackToMainView(): void {
    this.fullViewService.disableFullView();
  }

  goToPage(page: number): void {
    this.page = page;
    this.loadContracts();
  }

  formatCurrency(value: number): string {
    return `${this.currencySymbol}${value.toLocaleString()}`;
  }
}